import { Flex, List, Text } from '@mantine/core'
import { IconExternalLink } from '@tabler/icons-react'
import { useMemo } from 'react'
import { Link, useGetList } from 'react-admin'
import { BeneficiaryApiDTO, UserScopesApiDTO } from '../../codegen/generated/Api.schemas'
import { UsersHelper } from '../../helpers/Users.helper'

type UserScopeItem = {
  value: string
  label: string
  path?: string
}

type UserScopesValuesProps = {
  scopeKey: keyof UserScopesApiDTO
  scopes: string[]
}

export const UserScopesValues = ({ scopeKey, scopes }: UserScopesValuesProps) => {
  const beneficiariesQuery = useGetList<BeneficiaryApiDTO>(
    'beneficiaries',
    {
      sort: { field: 'name', order: 'ASC' },
      pagination: { page: 1, perPage: 10000 },
      filter: {
        id: scopes,
      },
    },
    {
      enabled: scopeKey === 'beneficiariesIds' && scopes.length > 0,
    },
  )
  const youtubeChannelsQuery = useGetList<BeneficiaryApiDTO>(
    'youtubeChannels',
    {
      sort: { field: 'name', order: 'ASC' },
      pagination: { page: 1, perPage: 10000 },
      filter: {
        id: scopes,
      },
    },
    {
      enabled: scopeKey === 'youtubeChannelsIds' && scopes.length > 0,
    },
  )

  const items: UserScopeItem[] = useMemo(() => {
    switch (scopeKey) {
      case 'cms':
        return UsersHelper.CMS
      case 'beneficiariesIds':
        return (beneficiariesQuery.data ?? [])?.map(b => ({
          value: b.id,
          label: b.name,
          path: `/beneficiaries/${b.id}/show`,
        }))
      case 'youtubeChannelsIds':
        return (youtubeChannelsQuery.data ?? [])?.map(yc => ({
          value: yc.id,
          label: yc.name,
          path: `/youtubeChannels/${yc.id}/show`,
        }))

      default:
        return []
    }
  }, [scopeKey, beneficiariesQuery.data, youtubeChannelsQuery.data])

  return (
    <List withPadding listStyleType="circle">
      {items
        .filter(item => scopes.includes(item.value))
        .map(item => (
          <List.Item key={item.value}>
            {item.path ? (
              <Link to={item.path} target="_blank">
                <Flex justify="center" align="center" gap={6}>
                  <Text pt={3}>{item.label}</Text>
                  <IconExternalLink size={14} color="var(--mantine-color-blue-6)" />
                </Flex>
              </Link>
            ) : (
              <Text>{item.label}</Text>
            )}
          </List.Item>
        ))}
    </List>
  )
}
